import axios from "axios";
import { UserSession } from "../user_session";
import * as Vars from "./Vars";

export class Functions {
  /**
   * All the available Event types, which can be used as trigger.
   */
  static event_types = [
    {
      text: "STORAGE_INSERT",
      value: 1000,
    },
    {
      text: "STORAGE_UPDATE",
      value: 1001,
    },
    {
      text: "STORAGE_DELETE",
      value: 1002,
    },
    {
      text: "AUTH_REGISTER",
      value: 2000,
    },
    {
      text: "AUTH_LOGIN",
      value: 2001,
    },
    {
      text: "FILES_UPLOAD",
      value: 3000,
    },
    {
      text: "FILES_FETCH",
      value: 3001,
    },
    {
      text: "FILES_TOKEN_UPDATE",
      value: 3002,
    },
    {
      text: "FILES_DELETE",
      value: 3003,
    },
  ];

  /**
   * All the available HTTP Methods, which can be triggered.
   */
  static url_methods = [
    {
      text: "GET",
      value: "get",
    },
    {
      text: "POST",
      value: "post",
    },
    {
      text: "PUT",
      value: "put",
    },
    {
      text: "DELETE",
      value: "delete",
    },
    {
      text: "PATCH",
      value: "patch",
    },
    {
      text: "OPTIONS",
      value: "options",
    },
  ];

  /**
   * The services, which can be used.
   */
  static url_services = [
    {
        text: "Webhook (Default)",
        value: "webhook",
        placeholder: "Params will not apply"
    },
    {
        text: "Azure",
        value: "azure",
        placeholder: "ex. {code: 'E5djz7MSuk2MQrEmz5fkdn23OAzFukzyd2w=='}"
    },
    {
        text: "Custom",
        value: "custom",
        placeholder: "ex. { customVarOne: '123', customVarTwo: 'test' }"
    }
  ]

  /**
   * Loads all the functions for the given project id.
   * @param {number} project_id The project ID, from where the configurations should be loaded.
   */
  static load(project_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "GET",
        params: {
          _console_token: UserSession.token,
          project: project_id,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Loads all the events of a given function.
   * @param {number} project_id The project ID, from where the configurations should be loaded.
   */
  static loadEvents(function_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "GET",
        params: {
          _console_token: UserSession.token,
          function: "events",
          function_id: function_id,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Loads all the events of a given function.
   * @param {number} project_id The project ID, from where the configurations should be loaded.
   */
  static loadUrls(function_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "GET",
        params: {
          _console_token: UserSession.token,
          function: "urls",
          function_id: function_id,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Creates a new function.
   * @param {int} project_id The project id, where the function should be stored.
   * @param {string} name The name of the new function.
   * @param {string} description The description of the new function.
   */
  static add(project_id, name, description) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "POST",
        data: {
          _console_token: UserSession.token,
          project_id: project_id,
          name: name,
          description: description,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Updates an existing function.
   * @param {int} function_id The function id, which should be updated.
   * @param {string} name The name of the new function.
   * @param {string} description The description of the new function.
   */
  static edit(function_id, name, description) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "PUT",
        data: {
          _console_token: UserSession.token,
          function: "update_function",
          function_id: function_id,
          name: name,
          description: description,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Deletes an existing function.
   * @param {int} function_id The function id, which should be deleted.
   */
  static delete(function_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "DELETE",
        data: {
          _console_token: UserSession.token,
          function: "delete_function",
          function_id: function_id,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Loads all the log for the given function.
   * @param {int} function_id The function id.
   */
  static loadLogs(function_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "GET",
        params: {
          _console_token: UserSession.token,
          function: "logs",
          function_id: function_id,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Registers a new event to a given function.
   * @param {int} function_id The id of the function.
   * @param {string} event_type The new event name type.
   */
  static registerEvent(function_id, event_type) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "POST",
        data: {
          _console_token: UserSession.token,
          function: "register_event",
          function_id: function_id,
          event_type: event_type,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Adds an URL to a given function
   * @param {int} function_id The id of the function.
   * @param {string} method The method, how the url should be triggered.
   * @param {string} url The url, which should be triggerd.
   * @param {string} secret A custom secret, which will be sent for every request.
   */
  static addUrl(function_id, method, url, secret) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "POST",
        data: {
          _console_token: UserSession.token,
          function: "add_url",
          function_id: function_id,
          method: method,
          url: url,
          secret: secret,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Edits an URL
   * @param {int} url_id The id of the url.
   * @param {string} method The method, how the url should be triggered.
   * @param {string} url The url, which should be triggerd.
   * @param {string} secret A custom secret, which will be sent for every request.
   * @param {string} service The service for this url
   * @param {string} additional_params Additional params as JSON object.
   */
  static editUrl(url_id, method, url, secret, service, additional_params) {
    var j_additional_params = "";
    try {
        j_additional_params = JSON.parse(additional_params);
        j_additional_params = additional_params;
    } catch (e) {
        alert("Additional parameters could not be saved. Not a valid JSON format.");
        j_additional_params = "";
    }

    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "PUT",
        data: {
          _console_token: UserSession.token,
          function: "update_url",
          url_id: url_id,
          method: method,
          url: url,
          secret: secret,
          service: service,
          params: j_additional_params
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Deletes an URL
   * @param {int} url_id The id of the url.
   */
  static deleteUrl(url_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "DELETE",
        data: {
          _console_token: UserSession.token,
          function: "delete_url",
          url_id: url_id,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }

  /**
   * Registers a new event to a given function.
   * @param {int} event_id The id of the event.
   */
  static unregisterEvent(event_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/functions/",
        method: "DELETE",
        data: {
          _console_token: UserSession.token,
          function: "unregister_event",
          event_id: event_id,
        },
      }).then(function(data) {
        resolve(data.data);
      });
    });
  }
}
